import { AlertTriangle, CheckCircle2, Clock, FileEdit, Lock, ShieldCheck, XCircle } from "lucide-react";

const STATUS = {
  pending_review: { label: "Pending Review", cls: "bg-blue-50 text-blue-800 border-blue-200", Icon: Clock },
  in_review: { label: "In Review", cls: "bg-blue-50 text-blue-800 border-blue-200", Icon: FileEdit },
  released: { label: "Approved & Released", cls: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
  rejected: { label: "Rejected", cls: "bg-rose-50 text-rose-800 border-rose-200", Icon: XCircle },
  draft: { label: "Draft", cls: "bg-slate-50 text-slate-700 border-slate-200", Icon: FileEdit },
};

export function StatusBadge({ status, testId }) {
  const s = STATUS[status] || { label: status || "No case", cls: "bg-slate-50 text-slate-600 border-slate-200", Icon: Clock };
  return (
    <span data-testid={testId || `status-badge-${status}`} className={`badge-pill ${s.cls}`}>
      <s.Icon className="w-3.5 h-3.5" /> {s.label}
    </span>
  );
}

const SEV = {
  red: "bg-red-50 text-red-800 border-red-200",
  warning: "bg-amber-50 text-amber-800 border-amber-200",
  ok: "bg-emerald-50 text-emerald-800 border-emerald-200",
  info: "bg-slate-50 text-slate-700 border-slate-200",
};

export function SeverityBadge({ severity }) {
  const label = { red: "Red Alert", warning: "Attention", ok: "Normal", info: "Info" }[severity] || severity;
  return (
    <span data-testid={`severity-badge-${severity}`} className={`badge-pill ${SEV[severity] || SEV.info} ${severity === "red" ? "pulse-red" : ""}`}>
      {severity === "red" && <AlertTriangle className="w-3.5 h-3.5" />} {label}
    </span>
  );
}

export function MissingDataBadge({ count }) {
  if (!count) return null;
  return (
    <span data-testid="missing-data-badge" className="badge-pill bg-amber-50 text-amber-800 border-amber-200">
      <AlertTriangle className="w-3.5 h-3.5" /> {count} missing
    </span>
  );
}

export function ConsentBadge({ active, label, testId }) {
  return (
    <span data-testid={testId} className={`badge-pill ${active ? "bg-green-50 text-green-800 border-green-200" : "bg-rose-50 text-rose-800 border-rose-200"}`}>
      {active ? <ShieldCheck className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />} {label}
    </span>
  );
}

export function RedAlertBanner({ alerts, testId = "red-alerts-banner" }) {
  if (!alerts?.length) return null;
  return (
    <div data-testid={testId} className="rise rounded-xl border border-red-200 bg-red-50 p-4 sm:p-5">
      <div className="flex items-center gap-2 text-red-800 font-semibold font-heading">
        <span className="w-2.5 h-2.5 rounded-full bg-red-600 pulse-red" /> {alerts.length} Red Alert{alerts.length > 1 ? "s" : ""} – requires doctor attention
      </div>
      <ul className="mt-3 space-y-2">
        {alerts.map((a, i) => (
          <li key={i} data-testid={`red-alert-item-${i}`} className="text-sm text-red-900 flex gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-red-600" />
            <div><span className="font-semibold">{a.title}</span> — {a.detail} <span className="genomic-code ml-1">{a.guideline}</span></div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Completeness({ value, testId = "completeness-indicator" }) {
  const color = value >= 90 ? "bg-emerald-500" : value >= 60 ? "bg-amber-500" : "bg-red-500";
  return (
    <div data-testid={testId} className="flex items-center gap-3">
      <div className="h-2 w-28 rounded-full bg-slate-200 overflow-hidden"><div className={`h-full ${color}`} style={{ width: `${value ?? 0}%` }} /></div>
      <span className="text-xs font-semibold text-slate-600">{value ?? 0}% complete</span>
    </div>
  );
}
